import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import type * as DialogPrimitive from "@radix-ui/react-dialog";
import type * as React from "react";
import { cn } from "../../lib/utils";

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Overlay
  > | null>;
}) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=open]:animate-in",
      "fixed inset-0 z-[70] bg-black/80",
      className
    )}
    ref={ref}
    {...props}
  />
);
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

type AlertDialogContentProps = React.ComponentPropsWithoutRef<
  typeof AlertDialogPrimitive.Content
> & {
  centered?: boolean;
  preventCloseOnOutsideClick?: boolean;
  preventCloseOnEscapeKey?: boolean;
  onInteractOutside?: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >["onInteractOutside"];
};

const AlertDialogContent = ({
  className,
  centered,
  children,
  ref,
  preventCloseOnOutsideClick = false,
  preventCloseOnEscapeKey = false,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: AlertDialogContentProps & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Content
  > | null>;
}) => {
  const handleEscapeKeyDown: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >["onEscapeKeyDown"] = (e) => {
    if (preventCloseOnEscapeKey) {
      e.preventDefault();
    }
    onEscapeKeyDown?.(e);
  };

  const handleInteractOutside: React.ComponentPropsWithoutRef<
    typeof DialogPrimitive.Content
  >["onInteractOutside"] = (e) => {
    if (preventCloseOnOutsideClick) {
      e.preventDefault();
    }
    onInteractOutside?.(e);
  };

  const ContentComponent =
    AlertDialogPrimitive.Content as typeof DialogPrimitive.Content;

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <ContentComponent
        className={cn(
          "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-[80] flex w-full max-w-[20rem] flex-col gap-6 rounded border border-zinc-200 bg-white p-6 shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05),0px_10px_15px_-3px_rgba(0,0,0,0.1)] duration-200 md:max-w-[30rem]",
          "data-[state=open]:zoom-in-95 data-[state=open]:fade-in-0 data-[state=open]:animate-in",
          "data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out",
          className
        )}
        onEscapeKeyDown={handleEscapeKeyDown}
        onInteractOutside={handleInteractOutside}
        ref={ref}
        {...props}
      >
        {children}
      </ContentComponent>
    </AlertDialogPortal>
  );
};
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

const AlertDialogHeader = ({
  className,
  centered,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { centered?: boolean }) => (
  <div
    className={cn(
      "flex flex-col gap-2",
      centered
        ? "items-center text-center"
        : "items-center text-center md:items-start md:text-left",
      className
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  centered,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { centered?: boolean }) => (
  <div
    className={cn(
      "flex gap-2",
      centered
        ? "flex-col items-stretch"
        : "flex-col-reverse items-stretch md:flex-row md:items-center md:justify-end",
      className
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Title
  > | null>;
}) => (
  <AlertDialogPrimitive.Title
    className={cn("font-semibold text-lg leading-tight", className)}
    ref={ref}
    {...props}
  />
);
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description> & {
  ref?: React.RefObject<React.ComponentRef<
    typeof AlertDialogPrimitive.Description
  > | null>;
}) => (
  <AlertDialogPrimitive.Description
    className={cn("text-sm leading-tight", className)}
    ref={ref}
    {...props}
  />
);
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = AlertDialogPrimitive.Action;
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = AlertDialogPrimitive.Cancel;
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
